/**
 * Ein Buch aufschlagen und wieder zuklappen.
 *
 * Die Grundidee
 *   Es gibt keinen Bildschirmwechsel. Die kommende Projektansicht ist von der
 *   ERSTEN Millisekunde an da – sie liegt als echte, lebende Seite im Buch,
 *   verkleinert auf die Größe des Einbands. Der Einband schwingt am Rücken
 *   auf und gibt sie frei, dann fährt die Kamera auf genau diese Seite zu,
 *   bis sie den Bildschirm füllt. Deshalb gibt es nichts umzuschalten: was am
 *   Ende dasteht, stand von Anfang an da, nur kleiner.
 *
 *   Der Router baut die neue Ansicht dafür nicht nach #app, sondern in die
 *   Buchseite (siehe bookSlot()). Erst wenn die Kamera angekommen ist,
 *   wandert genau dieser Knoten nach #app – dieselbe Ansicht, an derselben
 *   Stelle, in derselben Größe. Der Tausch ist per Konstruktion unsichtbar:
 *   es gibt keinen Frame, in dem etwas anderes zu sehen wäre.
 *
 * Der Aufbau, von hinten nach vorne
 *   .book__sheet   Das Blatt: bringt Kante und Schlagschatten mit. Ohne das
 *                  hätte die Seite dieselbe Farbe wie das Regal dahinter und
 *                  wäre gar nicht als Blatt zu erkennen.
 *   .book__view    Die Kamera. Verschiebt und skaliert alles darin von der
 *                  Größe des Einbands bis auf den ganzen Bildschirm.
 *   .book__crop    Der Beschnitt: der Einband ist hochkant, der Bildschirm
 *                  quer. Die Ansicht wird auf die HÖHE der Seite gebracht,
 *                  steht dann seitlich über und wird hier abgeschnitten.
 *   .book__inner   Hebt den Beschnitt für den Inhalt wieder auf, damit die
 *                  Ansicht darin unverzerrt bleibt. Hier hinein baut der
 *                  Router.
 *   .book__gutter  Der Schatten, den der Deckel in den Falz wirft. Er macht
 *                  sichtbar, dass hier zwei Lagen übereinander liegen.
 *   .book__leaf    Der Einband, der am Rücken aufschwingt. Vorderseite eine
 *                  Kopie des echten Einbands, Rückseite die Innenseite des
 *                  Deckels – dadurch sieht man jenseits von 90° Papier und
 *                  nicht das Nichts.
 *
 * Warum der Beschnitt so umständlich ist
 *   Der naheliegende Weg wäre clip-path auf der Seite. Der ist gemessen zu
 *   teuer: die Ansicht wird dann bei jedem Bild neu gemalt, und es geht
 *   selbst ohne Drosselung jedes zweite Bild verloren. Ein rechteckiges
 *   overflow: hidden auf einem verschobenen Element erledigt dagegen die
 *   Grafikeinheit. Der Preis dafür ist die Gegenskalierung: .book__crop wird
 *   waagerecht gestaucht, .book__inner genau umgekehrt gedehnt. Nach aussen
 *   ist keine Verzerrung zu sehen, weil sich beide exakt aufheben – siehe
 *   "Warum Stützstellen".
 *
 * Warum Stützstellen statt weniger Keyframes
 *   Stauchung und Gegendehnung heben sich nur auf, wenn sie im selben Moment
 *   zueinander passen. CSS interpoliert die beiden Werte aber unabhängig,
 *   und das Produkt zweier unabhängig interpolierter Zahlen ist nicht die
 *   interpolierte Eins. Deshalb wird die Bewegung in JS abgetastet (SAMPLES
 *   Stützstellen) und als dichte Keyframe-Folge übergeben: an jeder
 *   Stützstelle stimmt die Rechnung exakt, dazwischen liegt weniger als ein
 *   Bild. Nebenbei erledigt das einen Stolperstein der Web Animations:
 *   `easing` in den Optionen ist dort NICHT animation-timing-function,
 *   sondern die Zeitkurve des ganzen Effekts – sie verbiegt die Zeit, BEVOR
 *   die Offsets ausgewertet werden, und ein Abschnitt, der bei 0.34 beginnen
 *   soll, läuft dann schon nach einem Viertel der Zeit. Hier gibt es das
 *   Problem nicht mehr, weil zwischen den Stützstellen linear interpoliert
 *   wird und die Kurven in den Werten stecken.
 *
 * Warum der Rückweg keine eigenen Zahlen hat
 *   Er IST der Hinweg rückwärts: frameAt(1 - t). Damit können die beiden gar
 *   nicht auseinanderlaufen, und aus "Kamera heraus, offene Seite sichtbar,
 *   zuklappen" wird automatisch die Umkehrung von "aufschlagen, Seite
 *   sichtbar, Kamera heran".
 *
 * Kosten
 *   Bewegt werden ausschliesslich transform und opacity – kein Layout, kein
 *   Neuzeichnen, kein erneutes Bauen der Ansicht. Die Ansicht wird EINMAL
 *   gebaut und danach nur noch als fertige Ebene verschoben.
 */
import { h } from './dom.ts';

/**
 * Was eine Ansicht dem Übergang über das Buch im Regal verrät.
 *
 * Mehr als der Einband wird nicht gebraucht: aus seinem Rechteck folgt die
 * ganze Geometrie, und aus seinem Aussehen die Kopie, die aufschwingt.
 */
export interface BookTarget {
  cover: HTMLElement | null;
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/* ------------------------------------------------------------- Zeiten */

const OPEN_MS = 640;
/** Zurück darf etwas zügiger sein: man weiss ja schon, wohin es geht. */
const CLOSE_MS = 560;

/**
 * Stützstellen je Bewegung. 40 Stück auf 640 ms sind rund ein Bild Abstand –
 * dichter lohnt nicht, dünner würde die Gegenskalierung sichtbar
 * auseinanderlaufen.
 */
const SAMPLES = 40;

/** So lange wartet eine vorbereitete Bühne auf die Ansicht, die sie füllt. */
const WATCHDOG_MS = 1600;

/* --------------------------------------------- Abschnitte der Bewegung */

/** Der Deckel hebt sich an. */
const LIFT_END = 0.12;
/** Und schwingt auf. Endet vor der Kamera – die Überlappung macht es eins. */
const FLIP_END = 0.66;
/** Ab hier fährt die Kamera heran, mitten im Aufschlagen. */
const CAMERA_START = 0.34;
/** Der Deckel blendet weg, während er aus dem Bild schwingt. */
const FADE_START = 0.5;
const FADE_END = 0.82;
/** So schnell ist der Schatten im Falz da, und wie dunkel er wird. */
const GUTTER_IN = 0.2;
const GUTTER_MAX = 0.85;

/** Wie weit der Deckel aufschwingt. Knapp über die Waagerechte hinaus. */
const FLIP_DEGREES = 162;
/** Der kurze Hub, mit dem sich der Deckel zuerst anhebt. */
const LIFT_DEGREES = 6;

/* ------------------------------------------------------------- Kurven */

/**
 * Wertet eine cubic-bezier-Kurve aus, wie CSS sie versteht.
 *
 * Gebraucht, weil die Bewegung in JS abgetastet wird: es sollen dieselben
 * Kurven sein, die man auch in ein CSS-Easing schreiben würde, sonst fühlt
 * sich der Übergang anders an als der Rest der App.
 */
function bezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const atX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const atY = (t: number) => ((ay * t + by) * t + cy) * t;

  return (x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    // Intervallhalbierung: langsamer als das Newton-Verfahren, dafür immer
    // stabil – und bei 40 Stützstellen je Bewegung fällt das nicht auf.
    let low = 0;
    let high = 1;
    let t = x;
    for (let i = 0; i < 24; i += 1) {
      const value = atX(t);
      if (Math.abs(value - x) < 1e-5) break;
      if (value < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return atY(t);
  };
}

/** Der Deckel hebt sich an: kurz und bestimmt. */
const easeLift = bezier(0.25, 0.9, 0.35, 1);
/**
 * Das Aufschlagen. Vorne etwas Trägheit (ein Buchdeckel hat Masse), in der
 * Mitte Schwung, hinten weiches Auslaufen statt eines Anschlags.
 */
const easeOpen = bezier(0.42, 0.04, 0.18, 1);
/**
 * Die Kamerafahrt. Nimmt aus dem Aufschlagen Schwung mit, läuft lange aus
 * und kommt weich zum Stehen – bei etwa einem Drittel der Zeit sind drei
 * Viertel des Weges geschafft.
 */
const easeCamera = bezier(0.25, 0.7, 0.25, 1);

/* ------------------------------------------------------------ Zustand */

interface Stage {
  mode: 'open' | 'close';
  el: HTMLElement;
  view: HTMLElement;
  crop: HTMLElement;
  inner: HTMLElement;
  leaf: HTMLElement;
  hinge: HTMLElement;
  /*
   * Die beiden Seiten des Deckels. Die Deckkraft läuft ABSICHTLICH hier und
   * nicht am Deckel selbst: opacity unter 1 zwingt einen preserve-3d-Baum
   * flach, und genau in dem Moment würde die Perspektive springen. An den
   * Blättern des 3D-Baums ist sie harmlos.
   */
  faces: HTMLElement[];
  gutter: HTMLElement;
  sheet: HTMLElement;
  /** Das Rechteck des Einbands im Regal. Beim Zurückgehen erst später bekannt. */
  rect: Box | null;
  watchdog: number;
  animations: Animation[];
}

/** Es läuft immer höchstens ein Übergang. */
let stage: Stage | null = null;

/**
 * Der Einband im Regal, der gerade verdeckt ist.
 *
 * Verdeckt wird NUR der Einband, nicht der ganze Eintrag: der Name darunter
 * soll stehen bleiben und mit dem Regal verblassen, statt beim Antippen
 * schlagartig zu verschwinden.
 */
let pendingCover: HTMLElement | null = null;

/**
 * Wohin der Router die nächste Ansicht bauen soll.
 * null = wie immer nach #app.
 */
export function bookSlot(): HTMLElement | null {
  return stage?.mode === 'open' ? stage.inner : null;
}

/* ------------------------------------------------------------- Helfer */

/** Schalter in den Einstellungen (je Geraet): Buch-Animation aus. */
export const BOOK_MOTION_KEY = 'ls:book-motion';

export function bookMotionOn(): boolean {
  try {
    return localStorage.getItem(BOOK_MOTION_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setBookMotion(on: boolean): void {
  try {
    localStorage.setItem(BOOK_MOTION_KEY, on ? 'on' : 'off');
  } catch {
    /* gesperrt: bleibt fuer diese Sitzung wie es ist */
  }
}

function reducedMotion(): boolean {
  // Aus: sofort in die Ansicht (wie bei „Bewegung reduzieren“ im System).
  if (!bookMotionOn()) return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function appRoot(): HTMLElement | null {
  return document.getElementById('app');
}

function boxOf(el: HTMLElement): Box {
  const rect = el.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function usable(box: Box | null): box is Box {
  return box !== null && box.width > 1 && box.height > 1;
}

function place(el: HTMLElement, box: Box): void {
  el.style.left = `${box.left}px`;
  el.style.top = `${box.top}px`;
  el.style.width = `${box.width}px`;
  el.style.height = `${box.height}px`;
}

/* ---------------------------------------------------------- Geometrie */

/**
 * Die ganze Rechnung des Übergangs, abhängig vom Fortschritt der Kamera
 * (0 = Buch im Regal, 1 = Seite füllt den Bildschirm).
 *
 * Die Ansicht wird auf die HÖHE der Seite skaliert. Daraus folgt zweierlei:
 * senkrecht passt sie exakt, waagerecht steht sie über und wird
 * beschnitten. Und weil Seite und Ausschnitt aus derselben Zahl folgen, kann
 * zwischen ihnen nie eine Lücke entstehen – kein weißer Rand, kein Flackern.
 * Das ist nicht eingestellt, das folgt aus der Rechnung.
 */
function geometry(rect: Box, viewport: Box) {
  const vw = viewport.width;
  const vh = viewport.height;
  /** Maßstab der Ansicht im geschlossenen Buch. */
  const k0 = rect.height / vh;
  /** Wie breit die Ansicht dort wäre – breiter als der Einband. */
  const shown = vw * k0;
  const viewX = rect.left + rect.width / 2 - shown / 2;
  const viewY = rect.top;
  /** Anteil der Breite, der durch den Einband zu sehen ist. */
  const crop0 = rect.width / shown;
  /** Der Buchrücken, in Koordinaten der ungezoomten Ansicht. */
  const spineX = (rect.left - viewX) / k0;
  const spineY = vh / 2;
  const centerY = rect.top + rect.height / 2;
  const scaleAt = (p: number) => k0 + (1 - k0) * p;
  const cropAt = (p: number) => crop0 + (1 - crop0) * p;

  return {
    /** Die Kamera. */
    view(p: number): string {
      return `translate(${viewX * (1 - p)}px, ${viewY * (1 - p)}px) scale(${scaleAt(p)})`;
    },
    /** Wie viel der Breite stehen bleibt. 1 = kein Beschnitt mehr. */
    crop: cropAt,
    /** Was am Buchrücken klebt, also der Deckel: fährt mit der Kamera mit. */
    lift(p: number): string {
      const k = scaleAt(p);
      const x = viewX * (1 - p) + spineX * k;
      const y = viewY * (1 - p) + spineY * k;
      return `translate(${x - rect.left}px, ${y - centerY}px) scale(${k / k0})`;
    },
    /**
     * Die sichtbare Seite als Rechteck – für Blatt und Falzschatten, die
     * genau darauf liegen müssen und nicht bloss in der Nähe.
     */
    page(p: number): string {
      const k = scaleAt(p);
      const width = cropAt(p) * vw * k;
      const height = vh * k;
      const left = viewX * (1 - p) + (vw / 2) * k - width / 2;
      const top = viewY * (1 - p);
      return (
        `translate(${left - rect.left}px, ${top - rect.top}px) ` +
        `scale(${width / rect.width}, ${height / rect.height})`
      );
    },
  };
}

type Geometry = ReturnType<typeof geometry>;

/** Alles, was zu einem Zeitpunkt der Bewegung gilt. */
interface Frame {
  /** Winkel des Deckels in Grad, negativ = aufgeschlagen. */
  angle: number;
  /** Fortschritt der Kamerafahrt, 0..1. */
  camera: number;
  leafAlpha: number;
  gutterAlpha: number;
  shelfAlpha: number;
  shelfScale: number;
}

/**
 * Der Zustand der Bewegung zum Zeitanteil t (0..1) des AUFSCHLAGENS.
 * Der Rückweg fragt dieselbe Funktion bei 1 - t und ist damit exakt die
 * Umkehrung, ohne eigene Zahlen.
 */
function frameAt(t: number): Frame {
  const angle =
    t < LIFT_END
      ? -LIFT_DEGREES * easeLift(t / LIFT_END)
      : -(
          LIFT_DEGREES +
          (FLIP_DEGREES - LIFT_DEGREES) *
            easeOpen(Math.min(1, (t - LIFT_END) / (FLIP_END - LIFT_END)))
        );

  const camera = t <= CAMERA_START ? 0 : easeCamera((t - CAMERA_START) / (1 - CAMERA_START));

  const leafAlpha =
    t <= FADE_START ? 1 : t >= FADE_END ? 0 : 1 - (t - FADE_START) / (FADE_END - FADE_START);

  const gutterAlpha = GUTTER_MAX * Math.min(1, t / GUTTER_IN) * (1 - camera);

  /*
   * Das Regal tritt zurück, während die Kamera näherkommt: ein Hauch schon
   * beim Aufschlagen, damit sich das Blatt überhaupt vom Hintergrund abhebt
   * (beide haben dieselbe Grundfarbe), der Rest mit der Kamera. Es verblasst
   * in den Hintergrund der App, NICHT nach Schwarz – kein Schleier, keine
   * dunkle Fläche, nur Tiefe.
   */
  const early = Math.min(1, t / CAMERA_START);
  return {
    angle,
    camera,
    leafAlpha,
    gutterAlpha,
    shelfAlpha: 1 - 0.28 * early - 0.52 * camera,
    shelfScale: 1 + 0.01 * early + 0.05 * camera,
  };
}

/* -------------------------------------------------------------- Bühne */

function buildStage(mode: 'open' | 'close'): Stage {
  const sheet = h('div', { class: 'book__sheet' });
  const inner = h('div', { class: 'book__inner' });
  const crop = h('div', { class: 'book__crop' }, inner);
  const view = h('div', { class: 'book__view' }, crop);
  const gutter = h('div', { class: 'book__gutter' });

  const front = h('div', { class: 'book__face book__face--front' });
  const back = h('div', { class: 'book__face book__face--back' });
  const hinge = h('div', { class: 'book__hinge' }, front, back);
  const leaf = h('div', { class: 'book__leaf' }, hinge);

  // Reihenfolge = Tiefe: Blatt, darauf die Seite, darauf der Falzschatten,
  // darüber der Deckel.
  const el = h('div', { class: 'book', attrs: { 'aria-hidden': 'true' } }, sheet, view, gutter, leaf);
  document.body.append(el);

  const watchdog = window.setTimeout(() => abort(), WATCHDOG_MS);
  return {
    mode,
    el,
    view,
    crop,
    inner,
    leaf,
    hinge,
    faces: [front, back],
    gutter,
    sheet,
    rect: null,
    watchdog,
    animations: [],
  };
}

/** Setzt Deckel, Falz, Blatt und Blickpunkt auf das Rechteck des Einbands. */
function dressStage(current: Stage, rect: Box, cover: HTMLElement | null): void {
  place(current.leaf, rect);
  place(current.gutter, rect);
  place(current.sheet, rect);
  current.el.style.perspectiveOrigin = `${rect.left + rect.width / 2}px ${
    rect.top + rect.height / 2
  }px`;

  const front = current.faces[0];
  if (front && cover) {
    front.textContent = '';
    front.append(cover.cloneNode(true) as HTMLElement);
  }
}

/**
 * Schreibt einen einzelnen Zeitpunkt direkt in die Stile.
 *
 * Gebraucht für das Bild VOR dem Start: die Bühne muss schon richtig stehen,
 * bevor die erste Animation läuft, sonst blitzt ein Zwischending auf.
 */
function applyFrame(current: Stage, geo: Geometry, t: number): void {
  const f = frameAt(t);
  const cropX = geo.crop(f.camera);
  const page = geo.page(f.camera);
  current.view.style.transform = geo.view(f.camera);
  current.crop.style.transform = `scaleX(${cropX})`;
  current.inner.style.transform = `scaleX(${1 / cropX})`;
  current.leaf.style.transform = geo.lift(f.camera);
  current.hinge.style.transform = `rotateY(${f.angle}deg)`;
  current.sheet.style.transform = page;
  current.gutter.style.transform = page;
  current.gutter.style.opacity = String(f.gutterAlpha);
  for (const face of current.faces) face.style.opacity = String(f.leafAlpha);
}

/**
 * Die ganze Bewegung als dichte Keyframe-Folgen – siehe "Warum Stützstellen"
 * im Kopfkommentar.
 */
function keyframes(geo: Geometry, mode: 'open' | 'close') {
  const view: Keyframe[] = [];
  const crop: Keyframe[] = [];
  const inner: Keyframe[] = [];
  const leaf: Keyframe[] = [];
  const hinge: Keyframe[] = [];
  const sheet: Keyframe[] = [];
  const gutter: Keyframe[] = [];
  const face: Keyframe[] = [];
  const shelf: Keyframe[] = [];

  for (let i = 0; i <= SAMPLES; i += 1) {
    const offset = i / SAMPLES;
    // Der Rückweg ist der Hinweg rückwärts – eine Zeile, keine zweite Tabelle.
    const f = frameAt(mode === 'open' ? offset : 1 - offset);
    const cropX = geo.crop(f.camera);
    const page = geo.page(f.camera);

    view.push({ offset, transform: geo.view(f.camera) });
    crop.push({ offset, transform: `scaleX(${cropX})` });
    inner.push({ offset, transform: `scaleX(${1 / cropX})` });
    leaf.push({ offset, transform: geo.lift(f.camera) });
    hinge.push({ offset, transform: `rotateY(${f.angle}deg)` });
    sheet.push({ offset, transform: page });
    gutter.push({ offset, transform: page, opacity: f.gutterAlpha });
    face.push({ offset, opacity: f.leafAlpha });
    shelf.push({ offset, transform: `scale(${f.shelfScale})`, opacity: f.shelfAlpha });
  }
  return { view, crop, inner, leaf, hinge, sheet, gutter, face, shelf };
}

/**
 * Räumt die Bühne ab. Steckt noch eine Ansicht in der Buchseite, wird sie
 * vorher nach #app gerettet – eine gebaute Ansicht darf nie verloren gehen,
 * auch wenn der Übergang schiefgeht.
 */
function teardown(current: Stage): void {
  clearTimeout(current.watchdog);
  for (const animation of current.animations) animation.cancel();
  current.animations.length = 0;

  const root = appRoot();
  if (current.mode === 'open' && root) {
    const built = current.inner.firstElementChild;
    if (built) {
      root.textContent = '';
      root.append(built);
    }
  }
  if (root) {
    root.style.transform = '';
    root.style.opacity = '';
    root.style.willChange = '';
  }
  current.el.remove();
  if (stage === current) stage = null;
  // Die Zeichenebene hat sich beim Aufbau an der verkleinerten Seite
  // gemessen. Jetzt stimmen die Maße wieder.
  window.dispatchEvent(new Event('leadsheet:layout'));
}

/** Notbremse: Bühne weg, Ansicht retten, Zustand zurücksetzen. */
function abort(): void {
  if (!stage) return;
  const current = stage;
  const cover = pendingCover;
  pendingCover = null;
  teardown(current);
  if (cover) cover.style.visibility = '';
}

/** Die Animationen der Bühne starten und ihr Ende abwarten. */
function run(
  current: Stage,
  duration: number,
  specs: { el: HTMLElement; frames: Keyframe[] }[],
): Promise<void> {
  const animations = specs.map((spec) =>
    // Zwischen den Stützstellen linear – die Kurven stecken schon in den
    // Werten. Siehe Kopfkommentar.
    spec.el.animate(spec.frames, { duration, easing: 'linear', fill: 'both' }),
  );
  current.animations.push(...animations);
  const last = animations[animations.length - 1] as Animation;
  return last.finished.then(
    () => undefined,
    () => undefined, // ein Abbruch ist kein Fehler
  );
}

/** Die Bühnenteile und ihre Keyframes zusammenbringen. */
function specsFor(current: Stage, geo: Geometry, mode: 'open' | 'close') {
  const k = keyframes(geo, mode);
  const specs = [
    { el: current.sheet, frames: k.sheet },
    { el: current.view, frames: k.view },
    { el: current.crop, frames: k.crop },
    { el: current.inner, frames: k.inner },
    { el: current.gutter, frames: k.gutter },
    { el: current.leaf, frames: k.leaf },
    { el: current.hinge, frames: k.hinge },
    ...current.faces.map((face) => ({ el: face, frames: k.face })),
  ];
  const root = appRoot();
  if (root) {
    root.style.willChange = 'transform, opacity';
    specs.push({ el: root, frames: k.shelf });
  }
  return specs;
}

/* -------------------------------------------------------- Aufschlagen */

/**
 * Das Aufschlagen vorbereiten: Bühne stellen, Deckel setzen, Einband im
 * Regal verdecken. Danach darf navigiert werden – der Router baut die neue
 * Ansicht in die Buchseite (bookSlot) statt nach #app.
 *
 * Liefert false, wenn nicht animiert wird (weniger Bewegung eingestellt,
 * schon ein Übergang unterwegs, kein messbarer Einband). Dann navigiert der
 * Aufrufer einfach wie sonst auch.
 */
export function armBookOpen(target: BookTarget): boolean {
  if (stage || reducedMotion() || !target.cover) return false;
  const rect = boxOf(target.cover);
  if (!usable(rect)) return false;

  const current = buildStage('open');
  current.rect = rect;
  dressStage(current, rect, target.cover);

  // Der Einband steht jetzt doppelt da – das Original verschwindet.
  pendingCover = target.cover;
  target.cover.style.visibility = 'hidden';

  // Geschlossener Ausgangszustand, damit schon das erste Bild sitzt.
  applyFrame(current, geometry(rect, boxOf(current.el)), 0);

  stage = current;
  return true;
}

/**
 * Das Buch aufschlagen – gerufen, sobald die Ansicht in der Buchseite steht.
 *
 * Das Promise löst auf, wenn die Ansicht nach #app übergeben ist. Vorher
 * sollte niemand an ihr messen: sie liegt bis dahin verkleinert im Buch.
 */
export async function runBookOpen(): Promise<void> {
  const current = stage;
  if (!current || current.mode !== 'open' || !current.rect) return;
  clearTimeout(current.watchdog);
  current.watchdog = window.setTimeout(() => abort(), OPEN_MS + WATCHDOG_MS);

  /*
   * Ein Bild abwarten, bevor die Uhr läuft.
   *
   * Die Ansicht wurde gerade erst gebaut; ihr erstes Zeichnen ist der
   * teuerste Moment des ganzen Vorgangs. Startet die Animation im selben
   * Bild, frisst dieses Zeichnen die ersten rund 80 ms – der Deckel würde
   * dann nicht anheben, sondern schon halb offen erscheinen. Ein Bild
   * Vorlauf kostet nichts Sichtbares (es bewegt sich ohnehin noch nichts)
   * und macht aus einem Sprung einen sauberen Start.
   */
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  if (stage !== current) return;

  const geo = geometry(current.rect, boxOf(current.el));
  await run(current, OPEN_MS, specsFor(current, geo, 'open'));
  if (stage !== current) return; // zwischendurch abgebrochen

  pendingCover = null;
  teardown(current);
}

/* ---------------------------------------------------------- Zuklappen */

/**
 * Den Rückweg vorbereiten: die AKTUELLE Ansicht wandert unverändert in die
 * Buchseite. Auf dem Bildschirm passiert dabei nichts – sie liegt dort in
 * voller Größe an derselben Stelle. Danach darf navigiert werden; das Regal
 * baut sich unsichtbar dahinter auf.
 */
export function armBookClose(): boolean {
  if (stage || reducedMotion()) return false;
  const root = appRoot();
  if (!root || !root.firstElementChild) return false;

  const current = buildStage('close');
  // Kein Beschnitt, keine Skalierung: exakt das Bild von eben. Deckel, Falz
  // und Blatt bleiben unsichtbar, bis das Regal steht und sich messen lässt.
  current.view.style.transform = 'translate(0px, 0px) scale(1)';
  current.crop.style.transform = 'scaleX(1)';
  current.inner.style.transform = 'scaleX(1)';
  for (const face of current.faces) face.style.opacity = '0';
  current.gutter.style.opacity = '0';

  // Umhängen statt kopieren: Zeichenflächen, Bilder und Zustand bleiben so,
  // wie sie sind. Sichtbar ändert sich in diesem Moment nichts.
  while (root.firstChild) current.inner.append(root.firstChild);

  stage = current;
  return true;
}

/**
 * Das Buch zuklappen – gerufen, sobald das Regal steht und sich messen lässt.
 *
 * Erst hier ist bekannt, WO das Buch landen soll; deshalb die Zweiteilung.
 * Liefert false, wenn gar kein Rückweg unterwegs ist.
 */
export function finishBookClose(target: BookTarget): boolean {
  const current = stage;
  if (!current || current.mode !== 'close') return false;
  clearTimeout(current.watchdog);
  current.watchdog = window.setTimeout(() => abort(), CLOSE_MS + WATCHDOG_MS);

  const rect = target.cover ? boxOf(target.cover) : null;
  if (!usable(rect)) {
    // Leeres Regal: dann gibt es nichts, wohin das Buch zurückkönnte.
    void run(current, 220, [{ el: current.el, frames: [{ opacity: 1 }, { opacity: 0 }] }]).then(
      () => {
        if (stage === current) teardown(current);
      },
    );
    return true;
  }

  current.rect = rect;
  dressStage(current, rect, target.cover);
  pendingCover = target.cover;
  if (pendingCover) pendingCover.style.visibility = 'hidden';

  const geo = geometry(rect, boxOf(current.el));
  void run(current, CLOSE_MS, specsFor(current, geo, 'close')).then(() => {
    if (stage !== current) return;
    // Erst das echte Buch wieder zeigen, dann die Bühne weg: der Deckel liegt
    // in diesem Moment deckungsgleich darüber, es ist kein Wechsel zu sehen.
    if (pendingCover) pendingCover.style.visibility = '';
    pendingCover = null;
    teardown(current);
  });
  return true;
}
